import React, { Fragment, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PageModal from '../common/PageModal'
import Dropdown from '../common/Dropdown'
import ToggleSwitch from '../common/ToggleSwitch'
import SimpleButton from '../common/SimpleButton'
import { toast } from '../common/Toast'
import { CaretDownIcon } from '../../icons'
import { loadEmailTemplates, createNotificationSetting, updateNotificationSetting } from '../../actions/notifications'
import { getEmailTemplateContent } from '../../utils/requests/notificationsAPI'
import { acceptedTemplates } from './notificationEmailTemplates'

const AddEditNotificationSetting = (props) => {
  const { t } = useTranslation()
  const { selectedNotificationSetting, methods, groupEvents, closeModal, isLoading, showSpinner, hideSpinner, unusedGroupEvents } = props
  const [emailTemplates, setEmailTemplates] = useState([])
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [isUserEditable, setIsUserEditable] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [selectedGroupEvents, setSelectedGroupEvents] = useState(null)
  const [emailTemplatePreview, setEmailTemplatePreview] = useState(null)
  const [haveError, setHaveError] = useState({})

  useEffect(() => {
    if (props.emailTemplates.length === 0) {
      props.actions.loadEmailTemplates()
    }
  }, [props.actions])

  useEffect(() => {
    setEmailTemplates(props.emailTemplates)
  }, [props.emailTemplates])

  useEffect(() => {
    if (selectedNotificationSetting) {
      setTitle(selectedNotificationSetting.name)
      setDescription(selectedNotificationSetting.description)
      setIsUserEditable(selectedNotificationSetting.allowSuppress)
      setIsEnabled(selectedNotificationSetting.isEnabled)
      setSelectedEmailTemplate(selectedNotificationSetting.emailTemplateName)
      setSelectedType(selectedNotificationSetting.type)
      setSelectedGroupEvents(groupEvents.find((e) => e.id === selectedNotificationSetting.eventGroupId))
    }
  }, [selectedNotificationSetting])

  const notificationHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <p className="input-option">{t('status')}</p>
        <div className="status-option">
          <ToggleSwitch checked={isEnabled} onChange={() => setIsEnabled((prevValue) => !prevValue)} />
          <p className="text-status-option">{isEnabled ? t('active') : t('inactive')}</p>
        </div>
      </div>
    )
  }

  const notificationFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton className="submit-button" onClick={() => handleAddEditDeviceNotification()} disabled={isLoading}>
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const previewTemplateFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => setEmailTemplatePreview(null)}>
          {t('cancel')}
        </SimpleButton>
      </>
    )
  }

  const selectEmailTemplate = (emailTemplate, toggleVisibility) => {
    setSelectedEmailTemplate(emailTemplate)

    if (toggleVisibility) {
      toggleVisibility(false)
    }
  }

  const getEmailTemplateKey = (template) => {
    if (template === 'cycle_fault') {
      return 'cycle_fault_notification'
    }
    return template
  }

  const getNotificationEmailTemplates = (templates, acceptedArr) => {
    if (!templates) {
      return []
    }

    return templates.filter((e) => {
      return acceptedArr.includes(e)
    })
  }

  const selectType = (type) => {
    setSelectedType(type)
  }

  const selectGroupEvents = (groupEventKey) => {
    const keyName = groupEventKey.split('_').join(' ')
    const title = keyName.charAt(0).toUpperCase() + keyName.slice(1)
    setTitle(title)

    const groupEvent = unusedGroupEvents.find((groupEvent) => groupEvent.key === groupEventKey)
    setSelectedGroupEvents(groupEvent)
  }

  const handlePreviewEmailTemplate = () => {
    if (selectedEmailTemplate && !isLoading) {
      showSpinner()
      getEmailTemplateContent(selectedEmailTemplate)
        .then((data) => {
          setEmailTemplatePreview(data.HtmlPart)
          hideSpinner()
        })
        .catch(() => {
          toast.error(t('error.something_wrong'))
          hideSpinner()
        })
    }
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!title.trim()) {
      errors = { ...errors, title: true }
      isValid = false
    }

    if (!selectedType) {
      errors = { ...errors, selectedType: true }
      isValid = false
    }

    if (!selectedGroupEvents) {
      errors = { ...errors, selectedGroupEvents: true }
      isValid = false
    }

    if (!selectedEmailTemplate) {
      errors = { ...errors, selectedEmailTemplate: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleAddEditDeviceNotification = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    let bodyParams = {
      name: title.trim(),
      description: description.trim(),
      type: selectedType,
      allowSuppress: isUserEditable,
      emailTemplateName: selectedEmailTemplate,
      eventGroupId: selectedGroupEvents.id,
      isEnabled: isEnabled,
    }
    if (selectedNotificationSetting) {
      props.actions.updateNotificationSetting(selectedNotificationSetting.id, bodyParams)
    } else {
      props.actions.createNotificationSetting(bodyParams)
    }

    closeModal()
  }

  return (
    <Fragment>
      <PageModal
        toggle
        onToggle={() => closeModal()}
        title={selectedNotificationSetting ? t('events.edit-notification-setting-title') : t('events.add-notification-setting')}
        headerContent={notificationHeaderContent()}
        footerContent={notificationFooterContent()}
      >
        <div className="notification-modal-content">
          <div className="dropdown-wrapper">
            <div className="device-event-wrapper">
              <p className="input-option">{t('events.notification-type')}*</p>
              {!selectedNotificationSetting ? (
                <Dropdown
                  className="dropdown-input"
                  placeholder={t('events.select_event_group')}
                  options={unusedGroupEvents?.map((groupEvent) => ({ key: groupEvent.key, value: t([`filter_context.${groupEvent.key}`, groupEvent.key]) }))}
                  initialSelectedKey={selectedGroupEvents?.key}
                  onChange={(groupEvent) => selectGroupEvents(groupEvent.key)}
                  hasError={haveError['selectedGroupEvents']}
                />
              ) : (
                <input type="text" className="name-input name-input-plus" value={title} disabled />
              )}
            </div>
          </div>
          <p className="input-option">{t('description')}</p>
          <textarea className="description-input" value={description ?? ''} onChange={(e) => setDescription(e.target.value)}></textarea>
          <div className="edit-option-wrapper">
            <p className="input-edit-option">{t('events.user-editable')}</p>
            <ToggleSwitch checked={isUserEditable} onChange={() => setIsUserEditable((prevValue) => !prevValue)} />
            <p className="text-edit-option">{isUserEditable ? t('yes') : t('no')}</p>
          </div>
          <p className="edit-option-tooltip">{t('events.user-editable-message')}</p>
          <div className="dropdown-wrapper">
            <div className="type-wrapper">
              <p className="input-option">{t('events.delivery-method')}*</p>
              <Dropdown
                className="dropdown-input"
                placeholder={t('events.select_method')}
                options={methods?.map((method) => ({ key: method, value: t([`filter_context.${method}`, method]) }))}
                initialSelectedKey={selectedType}
                onChange={(method) => selectType(method.key)}
                hasError={haveError['selectedType']}
              />
            </div>
          </div>
          <div className="dropdown-wrapper">
            <div className="email-template-wrapper">
              <p className="input-option">{t('events.email-template')}*</p>
              <Dropdown
                className="dropdown-input"
                placeholder={t('events.select_email_template')}
                options={getNotificationEmailTemplates(emailTemplates, acceptedTemplates).map((emailTemplate) => ({
                  key: emailTemplate,
                  value: t([`filter_context.${getEmailTemplateKey(emailTemplate)}`, getEmailTemplateKey(emailTemplate)]),
                }))}
                initialSelectedKey={selectedEmailTemplate}
                onChange={(emailTemplate) => selectEmailTemplate(emailTemplate.key)}
                hasError={haveError['selectedEmailTemplate']}
              />
            </div>
            <div className="email-template-preview-button" onClick={() => handlePreviewEmailTemplate()}>
              <p className={'template-preview-tooltip' + (!selectedEmailTemplate ? ' disabled' : '')}>{t('events.preview-template')}</p>
            </div>
          </div>
        </div>
      </PageModal>
      {emailTemplatePreview && (
        <PageModal
          toggle
          onToggle={() => setEmailTemplatePreview(null)}
          title={t([`filter_context.${getEmailTemplateKey(selectedEmailTemplate)}`, getEmailTemplateKey(selectedEmailTemplate)])}
          footerContent={previewTemplateFooterContent()}
        >
          <div className="template-preview-modal-content">
            <div className="template-preview-content">
              <div dangerouslySetInnerHTML={{ __html: emailTemplatePreview }} />
            </div>
          </div>
        </PageModal>
      )}
    </Fragment>
  )
}

function stateToProps({ notifications }) {
  return {
    emailTemplates: notifications?.emailTemplates || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadEmailTemplates,
        createNotificationSetting,
        updateNotificationSetting,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditNotificationSetting)
